import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { MemberFeeDTO, MemberFeeAmountDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import Drawer from '../../components/common/Drawer';
import MemberFeeAmountTable from '../../components/tables/MemberFeeAmountTable';
import AddNewButton from '../../components/common/AddButton';
import Button from '../../components/common/Button';
import { confirmDelete } from '../../utils/sweetAlertUtils';

interface MemberFeeFormProps {
  initialValues: MemberFeeDTO;
  onSubmit: (data: MemberFeeDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface MemberFeeAmountWithIndex extends MemberFeeAmountDTO {
  arrayIndex?: number;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information'
  },
  feeAmounts: {
    id: 'fee-amounts',
    title: 'Fee Amounts'
  }
} as const;

const defaultMemberFeeAmount: MemberFeeAmountDTO = {
  fee: 0,
  joinFee: 0,
  effectiveDate: undefined,
};

const MemberFeeForm: React.FC<MemberFeeFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<MemberFeeDTO>(initialValues);
  const [memberFeeAmount, setMemberFeeAmount] = useState<MemberFeeAmountWithIndex>(defaultMemberFeeAmount);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);


  const validationRules: ValidationRules = {
    name: [
      { required: true, message: 'Name is required' }
    ]
  };

  const { errors, validate } = useFormValidation<MemberFeeDTO>(validationRules);

  const handleSave = () => {
    const formDataToValidate = {
      ...formData,
      memberFeeAmounts: formData.memberFeeAmounts || [],
    };
    if (validate(formDataToValidate)) {
      onSubmit(formData);
    }
  };

  const handleOptionSave = () => {
    if (memberFeeAmount) {
      setFormData((prev) => {
        const updatedAmounts = [...(prev.memberFeeAmounts || [])];

        // Helper to strip arrayIndex
        const stripArrayIndex = (obj: MemberFeeAmountWithIndex): MemberFeeAmountDTO => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { arrayIndex, ...rest } = obj;
          return rest;
        };

        if (memberFeeAmount.arrayIndex !== undefined) {
          // Update existing amount
          updatedAmounts[memberFeeAmount.arrayIndex] = stripArrayIndex(memberFeeAmount);
        } else if (memberFeeAmount.id) {
          // Update by ID
          const index = updatedAmounts.findIndex((a) => a.id === memberFeeAmount.id);
          if (index !== -1) {
            updatedAmounts[index] = stripArrayIndex(memberFeeAmount);
          } else {
            // Add new amount
            updatedAmounts.push({
              ...stripArrayIndex(memberFeeAmount),
              id: crypto.randomUUID ? crypto.randomUUID() : `temp-${Date.now()}`,
            });
          }
        } else {
          // Add new amount
          updatedAmounts.push({
            ...stripArrayIndex(memberFeeAmount),
            id: crypto.randomUUID ? crypto.randomUUID() : `temp-${Date.now()}`,
          });
        }

        return {
          ...prev,
          memberFeeAmounts: updatedAmounts,
        };
      });

      handleDrawerClose();
    }
  };

  const handleMemberFeeAmountEdit = (amountToEdit: MemberFeeAmountDTO) => {
    setMemberFeeAmount(amountToEdit);
    setIsDrawerOpen(true);
  };

  const handleMemberFeeAmountDelete = async (amountToDelete: MemberFeeAmountDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    setFormData((prev) => ({
      ...prev,
      memberFeeAmounts:
        prev.memberFeeAmounts?.filter(
          (amount) => amount.id !== amountToDelete.id,
        ) || [],
    }));
  };

  const handleDrawerClose = () => {
    setMemberFeeAmount(defaultMemberFeeAmount);
    setIsDrawerOpen(false);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Name"
          required={true}
          error={errors.name}
        />
        <FormField
          id="description"
          label="Description"
          type="text"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Description"
          required={false}
          error={errors.description}
        />
        <FormField
          id="isActive"
          label="Is Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Is Active"
          required={false}
          error={errors.isActive}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.feeAmounts.id}
        title={FORM_SECTIONS.feeAmounts.title}
        headerActions={
          <AddNewButton
            variant="secondary"
            label="Add Fee Amount"
            onClick={() => setIsDrawerOpen(true)}
          />
        }
      >
        <MemberFeeAmountTable
          data={formData.memberFeeAmounts || []}
          handleEdit={handleMemberFeeAmountEdit}
          handleDelete={handleMemberFeeAmountDelete}
          currentPage={currentPage}
          totalPages={Math.ceil((formData.memberFeeAmounts?.length || 0) / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={false}
        />
      </FormSection>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        title={memberFeeAmount.id ? 'Edit Fee Amount' : 'Add Fee Amount'}
      >
        <FormField
          id="fee"
          label="Fee"
          type="number"
          value={memberFeeAmount.fee || ''}
          onChange={(e) => handleChange(e, setMemberFeeAmount)}
          placeholder="Enter Fee"
          required={true}
        />
        <FormField
          id="joinFee"
          label="Join Fee"
          type="number"
          value={memberFeeAmount.joinFee ?? ''}
          onChange={(e) => handleChange(e, setMemberFeeAmount)}
          placeholder="Enter Join Fee"
          required={false}
        />
        <FormField
          id="effectiveDate"
          label="Effective Date"
          type="date"
          value={memberFeeAmount.effectiveDate || ''}
          onChange={(e) => handleChange(e, setMemberFeeAmount)}
          placeholder="Enter Effective Date"
          required={true}
          allowFutureDates={true}
        />
        <div className="flex gap-2 mt-4 justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={handleDrawerClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleOptionSave}
          >
            Save
          </Button>
        </div>
      </Drawer>
    </Form>
  );
};

export default MemberFeeForm;